import React from "react";
import ServicePage from "@/components/services/[slug]";
import { getCookie } from "utils/index";
import { decryptData } from "../utils/index";
import axios from "axios";

const Services = ({ authToken, pricingData, pricingDataPG, userTier, faqData }) => {
  // Handle cases where authToken or userTier is not passed
  const isLoggedIn = !!authToken;
  const userPlan = userTier; // Default to "free" if userTier is not provided

  return (
    <ServicePage
      authToken={isLoggedIn ? authToken : null}
      pricingData={pricingData}
      pricingDataPG={pricingDataPG}
      userTier={userPlan}
      faqData={faqData}
    />
  );
};

export async function getServerSideProps(context) {
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const authToken = context.req.cookies.authToken || null;
  let userTier = null;
  let pricingData = null;
  let pricingDataPG = null; // <-- Add PG pricing data

  try {
    if (authToken) {
      const response = await fetch(`${API_URL}/api/verify-user`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        // Get the current plan (last element in tier array)
        userTier = result.tier[result.tier.length - 1].toLowerCase();
      }
    }

    // UG pricing data
    const pricingResponse = await axios.get(
      `${API_URL}/api/fetch-services-data`
    );
    if (pricingResponse?.data?.data?.length > 0) {
      const fetchedData = pricingResponse.data.data[0];
      delete fetchedData._id;
      if (fetchedData.priceDetails) delete fetchedData.priceDetails._id;
      if (fetchedData.features) delete fetchedData.features._id;
      pricingData = fetchedData;
    }

    // PG pricing data - Updated to handle new structure
    const pricingResponsePG = await axios.get(
      `${API_URL}/api/fetch-pgservices-data`
    );
    if (pricingResponsePG?.data?.data?.length > 0) {
      const fetchedPGData = pricingResponsePG.data.data[0];
      
      // Remove _id fields from root and nested objects
      delete fetchedPGData._id;
      
      if (fetchedPGData.priceDetails) {
        delete fetchedPGData.priceDetails._id;
      }
      
      if (fetchedPGData.features) {
        delete fetchedPGData.features._id;
        
        // Remove _id from tools section if exists
        if (fetchedPGData.features.tools) {
          delete fetchedPGData.features.tools._id;
        }
        
        // Remove _id from analysis section if exists
        if (fetchedPGData.features.analysis) {
          delete fetchedPGData.features.analysis._id;
        }
        
        // Remove _id from postExamMentorship section if exists
        if (fetchedPGData.features.postExamMentorship) {
          delete fetchedPGData.features.postExamMentorship._id;
        }
      }
      
      pricingDataPG = fetchedPGData;
    }
  } catch (error) {
    console.error("Error fetching data:", error.message);
  }

  const faqData = [
    {
      question: "What tools and services does NEET Support offer?",
      answer: `
      <p>NEET Support offers a range of tools and counselling services through four plans – Free, Basic, Standard, and Premium.</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li><strong>Free Plan:</strong> Includes tools like rank predictor, exam analysis, and free webinars.</li>
        <li><strong>Basic Plan:</strong> Adds college predictor, score booster, seat matrix, and counselling videos.</li>
        <li><strong>Standard Plan:</strong> Includes college ranking, document checklist, physical guidebook, and mentor support.</li>
        <li><strong>Premium Plan:</strong> Offers full support with expert mentors, document help, application & choice filling, and Google Meet sessions.</li>
      </ul>
      <p>All plans are designed to offer transparent, affordable, and complete NEET counselling support based on your needs and budget.</p>
    `,
    },
    {
      question: "How does NEET Support help with college predictions?",
      answer: `
      <p>NEET Support helps with college predictions using its College Predictor Tool, which shows you the MBBS/BDS colleges you may get based on your NEET rank, category, state, and quota.</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>Uses real cut-off data from previous years to give accurate and personalized results.</li>
        <li>Helps you plan your counselling confidently and save time.</li>
      </ul>
    `,
    },
    {
      question: "Can NEET Support assist with document validation?",
      answer: `
      <p>Yes, NEET Support assists with document validation by helping students check and verify all necessary documents required for NEET counselling. This includes:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>Domicile Certificate</li>
        <li>Category/Reservation Proof</li>
        <li>NEET Admit Card & Scorecard</li>
        <li>ID Proofs and Academic Records</li>
        <li>NRI/Management Quota Documents (if applicable)</li>
      </ul>
      <p>This support ensures that all your documents are accurate and complete, helping you avoid delays or issues during the counselling process.</p>
    `,
    },
    {
      question: "What kind of personalized guidance does NEET Support provide?",
      answer: `
      <p>NEET Support provides personalized guidance to help each student make the best decisions during NEET counselling. This includes:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>One-on-one counselling based on your rank, category, state, and budget</li>
        <li>College shortlisting tailored to your preferences and chances</li>
        <li>Choice filling support to help you prioritize the right colleges</li>
        <li>Expert advice for All India and State quota strategies</li>
        <li>Guidance for special quotas like NRI, Management, or Reserved categories</li>
        <li>Mentorship before and after counselling for better planning</li>
      </ul>
      <p>This ensures every student gets step-by-step support suited to their unique profile.</p>
    `,
    },
    {
      question: "How does NEET Support simplify the NEET counselling process?",
      answer: `
      <p>NEET Support simplifies the NEET counselling process by offering step-by-step guidance and smart tools that make the process easy to follow. It helps students with:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>College prediction based on rank, category, and state</li>
        <li>Personalized choice filling support</li>
        <li>Alerts and reminders for important dates and deadlines</li>
        <li>Document checklist and validation help</li>
        <li>One-on-one expert counselling</li>
        <li>Real-time updates on seat allotments and cut-offs</li>
      </ul>
      <p>This reduces confusion, saves time, and helps students make confident and informed decisions.</p>
    `,
    },
    {
      question:
        "How does NEET Support handle counselling for both UG and PG medical courses?",
      answer: `
      <p>NEET Support handles counselling for both UG and PG medical courses by offering complete guidance tailored to each level:</p>
      <p><strong>For UG (MBBS/BDS):</strong></p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>College prediction based on NEET UG rank, category, and state.</li>
        <li>Help with All India Quota, State Quota, Private, Deemed & NRI seats.</li>
        <li>Step-by-step support for document preparation and choice filling.</li>
      </ul>
      <p><strong>For PG (MD/MS/DNB):</strong></p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>PG college predictor based on NEET PG rank and specialty preference.</li>
        <li>Guidance on AIQ and state PG counselling processes.</li>
        <li>Support with bond details, stipend info, and seat matrix.</li>
      </ul>
      <p>In both cases, NEET Support offers expert advice, document help, and personalized strategies to ensure a smooth and successful counselling experience.</p>
    `,
    },
    {
      question:
        "What are the main benefits of using NEET Support over other counselling services?",
      answer: `
      <p>NEET Support offers several key benefits over other counselling services, such as:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>Accurate College Predictions using real cut-off data from previous years</li>
        <li>Personalized Guidance based on your rank, category, state, and budget</li>
        <li>Support for All Quotas – AIQ, State, Private, Deemed, and NRI</li>
        <li>Step-by-step Help with documents, choice filling, and seat allotment</li>
        <li>Affordable and Transparent Plans with no hidden charges</li>
        <li>Expert Counsellors & Mentors for both UG and PG students</li>
        <li>Tools like Rank Predictor, College Compare, and Fee Matrix</li>
      </ul>
      <p>These features make NEET Support a reliable and student-friendly choice for stress-free NEET counselling.</p>
    `,
    },
    {
      question:
        "How does NEET Support ensure that students avoid common counselling pitfalls?",
      answer: `
      <p>NEET Support helps students avoid common counselling pitfalls by providing clear guidance and expert support at every step. It ensures:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>Accurate choice filling using personalized college lists</li>
        <li>Timely alerts for registration, deadlines, and document uploads</li>
        <li>Proper document validation to prevent rejection</li>
        <li>Understanding of seat allotment rules and quota eligibility</li>
        <li>Help with avoiding seat forfeiture or wrong college selection</li>
        <li>Expert review of preferences before final submission</li>
      </ul>
      <p>This reduces confusion and mistakes, helping students confidently secure the best possible seat.</p>
    `,
    },
    {
      question:
        "What kind of support does NEET Support offer during the counselling process?",
      answer: `
      <p>NEET Support provides complete, step-by-step assistance throughout the NEET counselling journey, including:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li>Document checklist & validation</li>
        <li>Personalized college shortlist based on your rank, state, category & budget</li>
        <li>Expert advice on choice filling and seat preferences</li>
        <li>Timely alerts for all rounds and deadlines</li>
        <li>1-on-1 counselling support via call, chat, and video</li>
        <li>Help with registration, application & seat confirmation</li>
        <li>Real-time updates on seat matrix, allotment trends & cut-offs</li>
      </ul>
    `,
    },
    {
      question: "How NEET Support Uses AI in Counselling Services",
      answer: `
      <p>NEET Support integrates AI tools to make counselling smarter and more accurate:</p>
      <ul class="text-sm" style="list-style-type: disc; margin-left: 20px;">
        <li><strong>AI-Powered College Predictor:</strong> Uses past 4+ years of cut-off data to suggest best-fit colleges based on rank, quota, and category.</li>
        <li><strong>Rank & Cut-off Analysis Tool:</strong> Predicts chances using trend analysis and closing rank movement.</li>
        <li><strong>Smart Choice Filling Tool:</strong> Suggests optimized college order to increase chances of seat allotment.</li>
        <li><strong>Real-Time AI Filters:</strong> Sorts colleges by fees, quota, state, and seat availability instantly.</li>
      </ul>
      <p>These AI features help students make informed decisions with clarity and confidence.</p>
    `,
    },
  ];

  return {
    props: {
      authToken,
      pricingData,
      pricingDataPG, // <-- Pass PG pricing data
      userTier,
      faqData,
    },
  };
}

export default Services;
